import { ChangeEventHandler, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import dayjs from 'dayjs';
import { toast } from 'sonner';
import { ConfirmationModal, CopyToClipboardButton } from 'wbs-components-lib';

import { Box } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';

import { ServerDataGrid, ServerDataGridPublicMethods } from '@shared/components';
import { DEFAULT_PAGE_LIMIT } from '@shared/constants';
import { useServerDataGridRefresh } from '@shared/hooks';
import { tableStyles } from '@shared/styles';

import {
  useDeleteTicketByIdMutation,
  useExpireTicketByIdsMutation,
  useLazyGetOrderedTicketsQuery,
  useSetContestTicketDefaultByIdMutation,
} from '@services';

import {
  AppDispatch,
  deleteTicket,
  getTicketsListSelector,
  getTicketsPageSelector,
  getTicketsTotalCountSelector,
  refreshTicketsTable,
  refreshTicketsTableSelector,
  setTicketsPage,
  setTicketsTotalCount,
  setTickets,
  updateTicket,
  setContestTicketDefault,
} from '@store';

import { BulkTicketDistributionModal } from '../BulkTicketDistributionModal';
import { TicketSettingsEditModal } from '../TicketSettingsEditModal';

import { TicketTableActionsMenu } from './TicketTableActionsMenu';

import { TicketStatus, TicketType } from 'v2/constants';
import { MasterKeyModel } from 'v2/types/MasterKeyModel';
import { TicketModel } from 'v2/types/TicketModel';

const { tableContainer } = tableStyles;

interface TicketSettingsTableProps {
  isUserService: boolean;
}

export const TicketSettingsTable = ({ isUserService }: TicketSettingsTableProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState<boolean>(false);
  const [isEditModalOpened, setIsEditModalOpened] = useState(false);
  const [isExpireModalOpened, setIsExpireModalOpened] = useState(false);
  const [isBulkDistributionModalOpen, setIsBulkDistributionModalOpen] = useState(false);
  const [processingTicket, setProcessingTicket] = useState<TicketModel | null>(null);

  const [deleteTicketById, { isLoading: isTicketDeleting }] = useDeleteTicketByIdMutation();
  const [expireTicketByIds, { isLoading: isTicketsExpiring }] = useExpireTicketByIdsMutation();
  const [setContestTicketDefaultById] = useSetContestTicketDefaultByIdMutation();

  const serverDataGridRef = useRef<ServerDataGridPublicMethods>(null);

  const [sort, setSort] = useState<GridSortModel>([{ field: 'date', sort: 'asc' }]);
  const [filter, setFilter] = useState<Record<string, string>>({});

  const refreshTicketsRefTable = async () => {
    await serverDataGridRef.current?.refreshList();
  };

  useServerDataGridRefresh(refreshTicketsTableSelector, refreshTicketsTable, refreshTicketsRefTable);

  const handleDeleteTicket = async () => {
    if (processingTicket === null) {
      return;
    }

    const { id: ticketId } = processingTicket;

    await deleteTicketById(ticketId);

    dispatch(deleteTicket(ticketId));

    handleDeleteModalClose();

    toast.success('Ticket has been deleted successfully');
  };

  const handleSetContestTicketDefault = (ticketId: string): ChangeEventHandler<HTMLInputElement> => {
    return async (e) => {
      const isDefault = e.target.checked;

      await setContestTicketDefaultById({ ticketId, isDefault });

      dispatch(setContestTicketDefault({ ticketId, isDefault }));

      toast.success('Tickets has been updated successfully');
    };
  };

  const handleExpireTicket = async () => {
    if (processingTicket === null) {
      return;
    }

    const { id: ticketId } = processingTicket;

    await expireTicketByIds([ticketId]);

    dispatch(updateTicket({ ...processingTicket, status: TicketStatus.EXPIRED }));

    handleExpireModalClose();

    toast.success('Ticket has been expired successfully');
  };

  const handleDeleteModalOpen = (ticket: TicketModel) => {
    setIsDeleteModalOpened(true);

    setProcessingTicket(ticket);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpened(false);

    setProcessingTicket(null);
  };

  const handleEditModalOpen = (ticket: TicketModel) => {
    setIsEditModalOpened(true);

    setProcessingTicket(ticket);
  };

  const handleEditModalClose = () => {
    setIsDeleteModalOpened(false);

    setProcessingTicket(null);
  };

  const handleExpireModalOpen = (ticket: TicketModel) => {
    setIsExpireModalOpened(true);

    setProcessingTicket(ticket);
  };

  const handleExpireModalClose = () => {
    setIsExpireModalOpened(false);

    setProcessingTicket(null);
  };

  const handleBulkDistributionModalOpen = (ticket: TicketModel) => {
    setProcessingTicket(ticket);

    setIsBulkDistributionModalOpen(true);
  };

  const handleBulkDistributionModalClose = () => {
    setIsBulkDistributionModalOpen(false);

    setProcessingTicket(null);
  };

  const ticketColumns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Id',
      minWidth: 80,
      maxWidth: 80,
      flex: 1,
      hideable: false,
      renderCell: (params: GridRenderCellParams<any, MasterKeyModel>) => {
        const {
          row: { id },
        } = params;

        return <CopyToClipboardButton>{id}</CopyToClipboardButton>;
      },
    },
    {
      field: 'type',
      headerName: 'Type',
      minWidth: 100,
      flex: 1,
      hideable: false,
    },
    {
      field: 'title',
      headerName: 'Title',
      minWidth: 100,
      flex: 1,
      hideable: false,
    },
    {
      field: 'description',
      headerName: 'Description',
      minWidth: 100,
      flex: 1,
      hideable: false,
    },
    {
      field: 'date',
      headerName: 'Date',
      minWidth: 100,
      flex: 1,
      hideable: false,
      valueGetter: (params: GridRenderCellParams<any, TicketModel>) => {
        return dayjs(params as unknown as string).format('YYYY-MM-DD HH:mm');
      },
    },
    {
      field: 'isDefault',
      headerName: 'Default',
      minWidth: 80,
      maxWidth: 80,
      flex: 1,
      hideable: false,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any, TicketModel>) => {
        const {
          row: { id, type, isDefault, status },
        } = params;

        return type === TicketType.CONTEST && status !== TicketStatus.EXPIRED ? (
          <Checkbox
            color="success"
            disabled={status === TicketStatus.EXPIRED || isUserService}
            checked={isDefault ?? false}
            onChange={handleSetContestTicketDefault(id)}
          />
        ) : null;
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 80,
      maxWidth: 80,
      flex: 1,
      hideable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<any, TicketModel>) => {
        const { row } = params;

        return (
          <TicketTableActionsMenu
            isUserService={isUserService}
            ticket={row}
            handleExpireTicketModalOpen={handleExpireModalOpen}
            handleTicketEditModalOpen={handleEditModalOpen}
            handleDeleteTicketModalOpen={handleDeleteModalOpen}
            handleBulkDistributionModalOpen={handleBulkDistributionModalOpen}
          />
        );
      },
    },
  ];

  return (
    <>
      <Box className={tableContainer}>
        <ServerDataGrid<TicketModel>
          ref={serverDataGridRef}
          columns={ticketColumns}
          pageSelector={getTicketsPageSelector}
          setPageAction={setTicketsPage}
          totalCountSelector={getTicketsTotalCountSelector}
          setTotalCountAction={setTicketsTotalCount}
          listSelector={getTicketsListSelector}
          setListAction={setTickets}
          limit={DEFAULT_PAGE_LIMIT}
          filter={{ ...filter, ...(isUserService ? { status: 'CREATED' } : undefined) }}
          setFilter={setFilter}
          sort={sort}
          setSort={setSort}
          useLazyListQuery={useLazyGetOrderedTicketsQuery}
        />
      </Box>
      <ConfirmationModal
        buttonConfirmedText="Delete"
        headerTitle="Delete Ticket"
        onSubmit={handleDeleteTicket}
        isOpened={isDeleteModalOpened}
        isLoading={isTicketDeleting}
        handleClose={handleDeleteModalClose}
      >
        Are you sure that you want to delete this ticket?
      </ConfirmationModal>
      <ConfirmationModal
        buttonConfirmedText="Expire"
        headerTitle="Expire Ticket"
        onSubmit={handleExpireTicket}
        isOpened={isExpireModalOpened}
        isLoading={isTicketsExpiring}
        handleClose={handleExpireModalClose}
      >
        Are you sure that you want to expire this ticket?
      </ConfirmationModal>
      {processingTicket !== null && (
        <TicketSettingsEditModal
          ticket={processingTicket}
          handleClose={handleEditModalClose}
          isOpened={isEditModalOpened}
        />
      )}
      {processingTicket !== null && (
        <BulkTicketDistributionModal
          isOpened={isBulkDistributionModalOpen}
          handleClose={handleBulkDistributionModalClose}
          ticket={processingTicket}
        />
      )}
    </>
  );
};
